import Telegram from '@/assets/icons/tg.svg?react'
import XIcon from '@/assets/icons/x.svg?react'
import { REF_INVITE_URL } from '@/env'
import { URLS } from '@/pages/urls'
import { Button, Input, Tooltip, TooltipContent, TooltipTrigger } from '@/ui-kit'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/ui-kit/components/Dialog'
import { Link } from '@/ui-kit/components/Link'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { Check, Copy, Pencil, X } from 'lucide-react'
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouteLink } from 'react-router-dom'
import { useCheckReferralCode, useReferralAliases, useSaveReferralCode } from '../api'

const REF_CODE_REGEXP = /^[A-Za-z0-9._\-~]+$/
const REF_LINK_FIXED_PART = `${REF_INVITE_URL}?invite=`
const TWITTER_POST = (code: string) =>
  `Join%20the%20@DataHiveAI%20airdrop%20simply%20by%20browsing%20the%20web%0A%0A` +
  `Use%20my%20link%20to%20earn%20extra%20bonus%20points:%20${REF_INVITE_URL}?invite=${code}%0A%0A` +
  `Get%20started%20now%20with%20the%20Chrome%20extension%20or%20Android%20app!`
const TG_MESSAGE = () =>
  `Join%20the%20DataHiveAI%20airdrop%20simply%20by%20browsing%20the%20web%0A%0A` +
  `Use%20my%20link%20to%20earn%20extra%20bonus%20points!%0A%0A` +
  `Get%20started%20now%20with%20the%20Chrome%20extension%20or%20Android%20app!`

export const InviteReferralModal = ({ dialogTrigger }: { dialogTrigger: ReactNode }) => {
  const { t } = useLingui()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [copyTooltip, setCopyTooltip] = useState('')

  const [refCode, setRefCode] = useState('')
  const { mutateAsync: saveRefCode } = useSaveReferralCode()
  const { mutateAsync: checkRefCode } = useCheckReferralCode()
  const { data: aliases, isFetched } = useReferralAliases()
  const currentAlias = useMemo(() => aliases?.items.find((al) => al.isCurrent), [aliases])

  useEffect(() => {
    if (isFetched) {
      setRefCode(currentAlias?.alias || '')
    }
  }, [currentAlias, isFetched])

  const onEditClick = useCallback(() => {
    setIsEditMode(true)
    inputRef.current?.focus()
  }, [])

  const onCancelEdit = useCallback(() => {
    setRefCode(currentAlias?.alias || '')
    setErrorText('')
    setIsEditMode(false)
  }, [currentAlias])

  const onRefCodeChange = useCallback(async (value: string) => {
    setRefCode(value)
    setErrorText('')
    if (value) {
      if (value.length < 3) {
        setErrorText('Min length is 3 characters')
      } else if (value.length > 50) {
        setErrorText('Max length is 50 characters')
      } else if (!REF_CODE_REGEXP.test(value)) {
        setErrorText('Only letters (A–Z, a–z), numbers (0–9), and these symbols are allowed: . _ - ~')
      } else {
        const result = await checkRefCode(value)
        if (!result.available) {
          setErrorText('This referral code is already taken')
        }
      }
    }
  }, [])

  const onSaveClick = useCallback(async () => {
    try {
      await saveRefCode(refCode)
      setIsEditMode(false)
    } catch (er) {
      setErrorText('Failed to save new ref code')
      console.log(er)
    }
  }, [refCode, saveRefCode])

  const onCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(REF_LINK_FIXED_PART + refCode)
      setCopyTooltip('Copied!')
    } catch {
      setCopyTooltip('Failed to copy URL!')
    } finally {
      setTimeout(() => {
        setCopyTooltip('')
      }, 1000)
    }
  }

  return (
    <Dialog>
      <DialogTrigger>{dialogTrigger}</DialogTrigger>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="!max-w-[548px]">
        <DialogHeader>
          <DialogTitle>
            <Trans>Share referral link</Trans>
          </DialogTitle>
          <DialogDescription className="mt-2">
            <Trans>Invite referrals and earn up to 20% of their activity!</Trans>
          </DialogDescription>
        </DialogHeader>
        <div className="relative mt-1 mb-4 flex max-md:flex-col">
          <Input
            className="w-[233px] max-md:w-full"
            inputClassName="flex-grow-0"
            rounded="start"
            isDisabled
            value={REF_LINK_FIXED_PART}
            defaultPristine={false}
          />
          <div className="relative flex-1">
            <Input
              ref={inputRef}
              className={clsx('-ml-6 h-full max-md:ml-0', !isEditMode && 'pointer-events-none')}
              rounded="end"
              placeholder={t`Ref code`}
              value={refCode}
              onChange={onRefCodeChange}
              defaultPristine={false}
              errorText={errorText}
              isError={!!errorText}
            />
            <div className="!absolute top-2 right-2 z-[1] flex gap-2">
              {!isEditMode ? (
                <>
                  <Button variant="bordered-gray" size="icon" onClick={onEditClick}>
                    <Pencil size={20} className="shrink-0" />
                  </Button>
                  <Tooltip open={!!copyTooltip}>
                    <TooltipTrigger>
                      <Button variant="bordered-gray" size="icon" onClick={onCopyClick}>
                        <Copy size={20} className="shrink-0" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{copyTooltip}</TooltipContent>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Button variant="bordered-gray" size="icon" onClick={onSaveClick}>
                    <Check size={20} className="shrink-0" />
                  </Button>
                  <Button variant="bordered-gray" size="icon" onClick={onCancelEdit}>
                    <X size={20} className="shrink-0" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        <DialogDescription>
          <Trans>Or share it on your social media:</Trans>
        </DialogDescription>
        <div className="m-auto flex gap-2">
          <Link
            variant="bordered-gray"
            title={t`Share on X`}
            href={`https://twitter.com/intent/tweet?text=${TWITTER_POST(refCode)}`}
            target="_blank"
            rel="noreferrer"
            disabled={isEditMode}
          >
            <XIcon width={20} height={20} />
          </Link>
          <Link
            variant="bordered-gray"
            title={t`Share on Telegram`}
            href={`https://t.me/share/url?text=${TG_MESSAGE()}&url=${REF_LINK_FIXED_PART + refCode}`}
            target="_blank"
            rel="noreferrer"
            disabled={isEditMode}
          >
            <Telegram />
          </Link>
        </div>
        <DialogDescription className="m-auto">
          <RouteLink
            className="group text-color-gray-90 flex items-center justify-center gap-2"
            to={URLS.BLOG_REFERRAL_PROGRAM}
            target="_blank"
            rel="noreferrer"
          >
            <span className="text-[var(--colorWhiteToBlack)] underline transition-colors duration-300 group-hover:text-[var(--color)]">
              <Trans>More information in our blog</Trans>
            </span>
          </RouteLink>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  )
}
